export const HUB_SUPPORT_TICKET_MAX_ATTACHMENTS = 6;
export const HUB_SUPPORT_TICKET_MAX_ATTACHMENT_BYTES = 2_400_000;

export const HUB_SUPPORT_TICKET_IMAGE_MIME = new Set(["image/png", "image/jpeg", "image/webp"]);

export const HUB_SUPPORT_TICKET_FILE_ACCEPT =
  "image/png,image/jpeg,image/webp,application/pdf,text/plain,text/csv,application/json,application/zip,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.png,.jpg,.jpeg,.webp,.pdf,.txt,.csv,.json,.zip,.xls,.xlsx,.docx";

const EXT_TO_MIME: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  pdf: "application/pdf",
  txt: "text/plain",
  csv: "text/csv",
  json: "application/json",
  zip: "application/zip",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

export type HubSupportTicketAttachmentKind = "image" | "file";

export interface HubSupportTicketAttachmentView {
  id: string;
  mimeType: string;
  fileName?: string;
  kind?: HubSupportTicketAttachmentKind;
  dataUrl: string;
}

export interface HubSupportLocalAttachment {
  id: string;
  mimeType: string;
  fileName: string;
  kind: HubSupportTicketAttachmentKind;
  dataUrl: string;
  dataBase64: string;
}

function base64FromDataUrl(dataUrl: string): { mimeType: string; dataBase64: string } | null {
  const match = /^data:([^;]+);base64,(.+)$/i.exec(dataUrl.trim());
  if (!match) return null;
  return { mimeType: match[1]!.trim().toLowerCase(), dataBase64: match[2]!.replace(/\s/g, "") };
}

function approxDecodedBase64Length(b64: string): number {
  return Math.floor((b64.length * 3) / 4);
}

function sanitizeFileName(value: string): string {
  const trimmed = value.replace(/[/\\]/g, "").replace(/\0/g, "").trim();
  if (trimmed.length === 0) return "attachment";
  return trimmed.slice(0, 180);
}

function mimeFromFile(file: File): string | null {
  let mime = file.type.trim().toLowerCase();
  if (mime === "image/jpg") mime = "image/jpeg";
  if (mime === "" || mime === "application/octet-stream") {
    const name = file.name.toLowerCase();
    const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "";
    mime = EXT_TO_MIME[ext] ?? "";
  }
  if (HUB_SUPPORT_TICKET_IMAGE_MIME.has(mime) || Object.values(EXT_TO_MIME).includes(mime)) {
    return mime;
  }
  return null;
}

export function hubSupportTicketAttachmentKind(mimeType: string): HubSupportTicketAttachmentKind {
  return HUB_SUPPORT_TICKET_IMAGE_MIME.has(mimeType) ? "image" : "file";
}

export function hubSupportLocalAttachmentFromDataUrl(
  dataUrl: string,
  fileName: string,
): HubSupportLocalAttachment | null {
  const parsed = base64FromDataUrl(dataUrl);
  if (!parsed) return null;
  if (!HUB_SUPPORT_TICKET_IMAGE_MIME.has(parsed.mimeType)) return null;
  if (approxDecodedBase64Length(parsed.dataBase64) > HUB_SUPPORT_TICKET_MAX_ATTACHMENT_BYTES) return null;
  return {
    id: crypto.randomUUID(),
    mimeType: parsed.mimeType,
    fileName: sanitizeFileName(fileName),
    kind: "image",
    dataUrl: dataUrl.trim(),
    dataBase64: parsed.dataBase64,
  };
}

export async function fileToHubSupportLocalAttachment(file: File): Promise<HubSupportLocalAttachment | null> {
  const mime = mimeFromFile(file);
  if (mime == null) return null;
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });
  const parsed = base64FromDataUrl(dataUrl);
  if (!parsed) return null;
  if (approxDecodedBase64Length(parsed.dataBase64) > HUB_SUPPORT_TICKET_MAX_ATTACHMENT_BYTES) return null;
  return {
    id: crypto.randomUUID(),
    mimeType: mime,
    fileName: sanitizeFileName(file.name || "attachment"),
    kind: hubSupportTicketAttachmentKind(mime),
    dataUrl,
    dataBase64: parsed.dataBase64,
  };
}

export function openHubSupportTicketAttachment(dataUrl: string, fileName = "attachment"): void {
  const match = /^data:([^;]+);base64,(.+)$/i.exec(dataUrl.trim());
  if (!match) return;
  const mimeType = match[1]!.trim();
  const base64 = match[2]!.replace(/\s/g, "");
  let binary: string;
  try {
    binary = atob(base64);
  } catch {
    return;
  }
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const blob = new Blob([bytes], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (opened) {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000);
    return;
  }
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
}

export function hubSupportTicketAttachmentApiPayload(row: HubSupportLocalAttachment): {
  mimeType: string;
  dataBase64: string;
  fileName: string;
} {
  return { mimeType: row.mimeType, dataBase64: row.dataBase64, fileName: row.fileName };
}
